import { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useDialog } from "app/hook";
import { recordEvent } from "app/analytics";
import { RootState, AppDispatch } from "app/store";
import { DialogType, AnalyticsEventType } from "app/types";
import { actions as configActions } from "app/configSlice";
import { UserAvatar, PlanBadge } from "common/elements";
import { useScale, usePlaySound, btnDelay } from "common/utils";
import { usePlan } from "features/subscription";
import { ParentPageTab } from "features/parent/types";
import i18n, { setParentLocale, restoreChildLocale } from "features/parent/i18n";
import { markParentChatRoomMessage } from "./api";
import { ParentWarningModal } from "./components";
import { selectAllParentChatRoomMessageSync } from "./slice";
import {
  OtherTab,
  AccountTab,
  MessageTab,
  PlayerTab,
  SubscriptionTab,
  NotificationTab,
} from "./tabs";

export const ParentSettingPage = () => {
  const plan = usePlan();
  const play = usePlaySound();
  const navigate = useNavigate();
  const handleDialog = useDialog();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const { width, height, scale } = useScale();
  const users = useSelector((state: RootState) => state.user.users);
  const parent = useSelector((state: RootState) => state.user.parent);
  const parentLocale = useSelector((state: RootState) => state.config.parentConfig.locale);
  const childLocale = useSelector((state: RootState) => state.config.userConfig.locale);
  const parentChatRoomMessageSync = useSelector(selectAllParentChatRoomMessageSync);
  const justAuthenticated = useSelector((state: RootState) => state.config.justAuthenticated);
  // 子ども0人（active === null）の場合はプレイヤータブをデフォルトに
  const [activeTab, setActiveTab] = useState<ParentPageTab>(
    users.length > 1 ? ParentPageTab.ACCOUNT : ParentPageTab.PLAYER,
  );
  const notification = useSelector((state: RootState) => state.config.notification.parents);
  const scrollRef = useRef<HTMLElement>(null);
  const [showNoPlayerWarning, setShowNoPlayerWarning] = useState(false);

  const scrollToTop = () => {
    scrollRef.current?.scrollTo(0, 0);
  };

  const handleBack = () => {
    play();
    btnDelay(() => {
      if (users.length > 1) {
        navigate("/top");
      } else {
        // プレイヤーがいない場合は警告ダイアログを表示
        setShowNoPlayerWarning(true);
      }
    });
  };

  const handleNoPlayerConfirm = () => {
    setShowNoPlayerWarning(false);
    setActiveTab(ParentPageTab.PLAYER);
  };

  const handleTabChange = (tab: ParentPageTab) => {
    play();
    recordEvent(AnalyticsEventType.PARENT_TAB_OPEN, { tab });
    setActiveTab(tab);
  };

  const handleOpenLink = (url: string) => {
    play();
    if ("ReactNativeWebView" in window) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ command: "openLink", data: { url: url } }),
      );
    } else {
      window.open(url, "_blank");
    }
  };

  // 既読マークをサーバーに送信（ページ離脱時）+ 子どもの言語に戻す
  useEffect(() => {
    return () => {
      dispatch(markParentChatRoomMessage());
      restoreChildLocale(childLocale);
    };
  }, [dispatch, childLocale]);

  // プレイヤー0人 かつ 認証直後でない場合にParentGateを表示
  useEffect(() => {
    if (users.length === 1 && !justAuthenticated) {
      handleDialog({
        type: DialogType.PARENT_GATE,
        value: true,
        args: { noPlayerMode: "true" },
      });
    }
    // justAuthenticatedフラグをリセット
    if (justAuthenticated) {
      dispatch(configActions.setJustAuthenticated(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setParentLocale(parentLocale);
  }, [parentLocale]);

  // クエリパラメータでタブ切り替え
  useEffect(() => {
    // tabパラメータでタブ切り替え
    const tabParam = searchParams.get("tab");
    if (tabParam && Object.values(ParentPageTab).includes(tabParam as ParentPageTab)) {
      setActiveTab(tabParam as ParentPageTab);
    }
  }, [searchParams]);

  const hasNewNotification = notification.some((n) => dayjs().diff(n.published_at, "day") < 7);

  const hasUnreadMessage =
    parentChatRoomMessageSync.filter(
      (sync) =>
        dayjs(sync.latestMessageCreatedAt, "YYYY-MM-DDTHH:mm:ssZ").diff(
          dayjs(sync.latestReadAt, "YYYY-MM-DDTHH:mm:ssZ"),
        ) > 0 ||
        (sync.latestReadAt === null && sync.latestMessageCreatedAt !== null),
    ).length > 0;

  return (
    <div className="flex-col-el flex-center w-full h-full bg-gray2-30 z-[9999]">
      <div className="flex-col-el w-full flex-1 overflow-hidden">
        <div className="flex-col-el w-full h-full">
          {/* ヘッダー */}
          <div style={{ height: 90 * scale }} className="flex-col-el flex-center">
            <header
              style={{ transform: `scale(${scale})`, width: width / scale }}
              className="flex-row-el h-[90px] items-center px-6 py-4 border-b border-outline bg-white"
            >
              <button
                className="flex-row-el items-center justify-center gap-1 px-3 h-[64px] bg-gray2-30 rounded-lg text-textcolor-black cursor-pointer border-none hover:bg-gray2-40 transition-all"
                onClick={handleBack}
              >
                <span className="material-symbols-outlined text-[28px]">play_circle</span>
                <span className="text-[16px]">{i18n.t("MSG_PARENT_PAGE_LAUNCH_APP")}</span>
              </button>

              <div className="flex-row-el items-center gap-4 ml-6 flex-1">
                <UserAvatar size={72} icon={parent?.icon} />
                <span className="text-[30px] text-textcolor-black">
                  {parent?.display_name || "-"}
                </span>
                <PlanBadge plan={plan} size="small" forParent />
                <span className="text-[21px] text-gray2-100 ml-auto">{parent?.email || "-"}</span>
              </div>
            </header>
          </div>

          {/* タブナビゲーション */}
          <div style={{ height: 80 * scale }} className="flex-col-el flex-center bg-white/40">
            <nav
              style={{ transform: `scale(${scale})`, width: width / scale }}
              className="flex-row-el items-center h-[80px] gap-2 px-6 py-4"
            >
              <button
                className={`flex-1 flex-row-el flex-center gap-2 py-3 border-none rounded-[12px] text-[24px] cursor-pointer transition-all ${
                  activeTab === ParentPageTab.ACCOUNT
                    ? "bg-ink text-white"
                    : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
                }`}
                onClick={() => handleTabChange(ParentPageTab.ACCOUNT)}
              >
                <span className="material-symbols-outlined text-xl">person</span>
                {i18n.t("MSG_PARENT_PAGE_TAB_ACCOUNT")}
              </button>
              <button
                className={`flex-1 flex-row-el flex-center gap-2 py-3 border-none rounded-[12px] text-[24px] cursor-pointer transition-all ${
                  activeTab === ParentPageTab.PLAYER
                    ? "bg-ink text-white"
                    : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
                }`}
                onClick={() => handleTabChange(ParentPageTab.PLAYER)}
              >
                <span className="material-symbols-outlined text-xl">group</span>
                {i18n.t("MSG_PARENT_PAGE_TAB_PLAYER")}
              </button>
              <button
                className={`flex-1 flex-row-el flex-center relative gap-2 py-3 border-none rounded-[12px] text-[24px] cursor-pointer transition-all ${
                  activeTab === ParentPageTab.MESSAGE
                    ? "bg-ink text-white"
                    : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
                }`}
                onClick={() => handleTabChange(ParentPageTab.MESSAGE)}
              >
                <span className="material-symbols-outlined text-xl">chat</span>
                {i18n.t("MSG_PARENT_PAGE_TAB_MESSAGE")}
                {hasUnreadMessage && (
                  <div className="flex-col-el flex-center absolute right-[5px] top-0.5 bg-danger rounded-full w-[20px] h-[20px] z-50" />
                )}
              </button>
              <button
                className={`flex-1 flex-row-el flex-center gap-2 py-3 border-none rounded-[12px] text-[24px] cursor-pointer transition-all ${
                  activeTab === ParentPageTab.SUBSCRIPTION
                    ? "bg-ink text-white"
                    : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
                }`}
                onClick={() => handleTabChange(ParentPageTab.SUBSCRIPTION)}
              >
                <span className="material-symbols-outlined text-xl">credit_card</span>
                {i18n.t("MSG_PARENT_PAGE_TAB_SUBSCRIPTION")}
              </button>
              <button
                className={`flex-1 flex-row-el flex-center gap-2 py-3 border-none rounded-[12px] text-[24px] cursor-pointer transition-all ${
                  activeTab === ParentPageTab.NOTIFICATION
                    ? "bg-ink text-white"
                    : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
                }`}
                onClick={() => handleTabChange(ParentPageTab.NOTIFICATION)}
              >
                <span className="material-symbols-outlined text-xl">notifications</span>
                {i18n.t("MSG_PARENT_PAGE_TAB_NOTIFICATION")}
                {hasNewNotification && (
                  <span className="inline-flex items-center justify-center px-2 h-[20px] bg-danger text-white rounded-[10px] text-xs">
                    NEW
                  </span>
                )}
              </button>
              <button
                className={`flex-1 flex-row-el flex-center gap-2 py-3 border-none rounded-[12px] text-[24px] cursor-pointer transition-all ${
                  activeTab === ParentPageTab.OTHER
                    ? "bg-ink text-white"
                    : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
                }`}
                onClick={() => handleTabChange(ParentPageTab.OTHER)}
              >
                <span className="material-symbols-outlined text-xl">more_horiz</span>
                {i18n.t("MSG_PARENT_PAGE_TAB_OTHER")}
              </button>
            </nav>
          </div>

          {/* メインコンテンツ */}
          <div
            style={{ height: height - (90 + 80 + 52) * scale }}
            className="flex-col-el items-center justify-start w-full bg-white/40"
          >
            <main
              ref={scrollRef}
              style={{
                width: width / scale,
                height: (height - (90 + 80 + 52) * scale) / scale,
                transform: `scale(${scale})`,
              }}
              className="flex-col-el shrink-0 p-6 origin-top overflow-y-auto invisible-scrollbar"
            >
              {activeTab === ParentPageTab.ACCOUNT && <AccountTab />}
              {activeTab === ParentPageTab.PLAYER && <PlayerTab scrollToTop={scrollToTop} />}
              {activeTab === ParentPageTab.MESSAGE && <MessageTab />}
              {activeTab === ParentPageTab.SUBSCRIPTION && <SubscriptionTab />}
              {activeTab === ParentPageTab.NOTIFICATION && <NotificationTab />}
              {activeTab === ParentPageTab.OTHER && <OtherTab />}
            </main>
          </div>
        </div>
      </div>

      {/* フッター */}
      <div style={{ height: 52 * scale }} className="flex-col-el flex-center w-full bg-white/40">
        <footer
          style={{ transform: `scale(${scale})` }}
          className="flex-row-el flex-center w-[1280px] h-[52px] gap-4 px-6 py-4 text-sm text-gray2-100"
        >
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/product/")}
          >
            {i18n.t("MSG_SETTING_FOOTER_GO_TO_SITE")}
          </span>
          <span>|</span>
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/privacy-policy/")}
          >
            {i18n.t("MSG_SETTING_FOOTER_TERMS_OF_SERVICE")}
          </span>
          <span>|</span>
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/privacy-policy/")}
          >
            {i18n.t("MSG_SETTING_FOOTER_PRIVACY_POLICY")}
          </span>
          <span>|</span>
          <span
            className="cursor-pointer hover:text-blue-100 hover:underline transition-colors"
            onClick={() => handleOpenLink("https://inexus-co.com/contact/")}
          >
            {i18n.t("MSG_SETTING_FOOTER_INQUIRY")}
          </span>
        </footer>
      </div>

      <ParentWarningModal
        open={showNoPlayerWarning}
        title={i18n.t("MSG_PARENT_PAGE_NO_PLAYER_WARNING_TITLE")}
        message={i18n.t("MSG_PARENT_PAGE_NO_PLAYER_WARNING_MESSAGE")}
        icon="warning"
        onClose={handleNoPlayerConfirm}
      />
    </div>
  );
};
